import { useEffect } from "preact/hooks";
import { useState } from "preact/hooks";
import { getIngestBaseUrl } from "../lib/public-url.js";
import PublicHeader from "../components/PublicHeader.js";
import PublicFooter from "../components/PublicFooter.js";

type Platform = "shopify" | "make" | "zapier" | "n8n";

const DEFAULT_PLATFORM: Platform = "make";

function isPlatform(value: string): value is Platform {
  return value === "shopify" || value === "make" || value === "zapier" || value === "n8n";
}

function readPlatformHash(hash: string): Platform | null {
  const value = hash.replace(/^#/, "").trim().toLowerCase();
  return isPlatform(value) ? value : null;
}

function replaceGuideHash(platform: Platform): void {
  if (typeof window === "undefined") return;
  const nextPath = `${window.location.pathname}${window.location.search}#${platform}`;
  window.history.replaceState(window.history.state, "", nextPath);
}

function StepNumber({ n }: { n: number }) {
  return (
    <span class="mr-3 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-teal-600 text-sm font-bold text-white">
      {n}
    </span>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: preact.ComponentChildren;
}) {
  return (
    <div class="flex items-start gap-1">
      <StepNumber n={n} />
      <div>
        <h3 class="text-base font-semibold text-slate-900">{title}</h3>
        <div class="mt-1 text-sm leading-relaxed text-slate-600">
          {children}
        </div>
      </div>
    </div>
  );
}

function CodeSnippet({ children }: { children: string }) {
  return (
    <pre class="mt-2 overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-800">
      <code>{children.trim()}</code>
    </pre>
  );
}

function Callout({
  color,
  title,
  children,
}: {
  color: "amber" | "blue" | "teal" | "slate";
  title: string;
  children: preact.ComponentChildren;
}) {
  const styles: Record<string, string> = {
    amber: "border-amber-200 bg-amber-50",
    blue: "border-blue-200 bg-blue-50",
    teal: "border-teal-100 bg-teal-50",
    slate: "border-slate-200 bg-slate-50",
  };
  const titleStyles: Record<string, string> = {
    amber: "text-amber-900",
    blue: "text-blue-900",
    teal: "text-teal-800",
    slate: "text-slate-900",
  };
  const bodyStyles: Record<string, string> = {
    amber: "text-amber-800",
    blue: "text-blue-800",
    teal: "text-teal-700",
    slate: "text-slate-600",
  };
  return (
    <div class={`rounded-lg border p-4 ${styles[color]}`}>
      <p class={`text-sm font-medium ${titleStyles[color]}`}>{title}</p>
      <div class={`mt-1 text-sm ${bodyStyles[color]}`}>{children}</div>
    </div>
  );
}

function PlatformTab({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      class={`rounded-t-lg border-b-2 px-4 py-2 text-sm font-medium transition ${
        active
          ? "border-teal-600 text-teal-700"
          : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
      }`}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Shopify Guide
// ---------------------------------------------------------------------------

function ShopifyGuide() {
  const ingestBaseUrl = getIngestBaseUrl();

  return (
    <div class="space-y-8">
      <div class="space-y-3">
        <p class="text-sm text-slate-600">
          Shopify order webhooks commonly exceed 1 MB — stores with metafields,
          variant images, and discount codes can push 5–10 MB per event. That's
          enough to silently truncate or drop payloads in Zapier (6 MB limit),
          Make (5 MB), and n8n (16 MB default). This guide puts webhookstorage
          between Shopify and your automation so every byte arrives intact.
        </p>
        <Callout color="amber" title="Shopify can't send custom headers">
          <p>
            Shopify admin webhooks don't support custom HTTP headers, so you
            can't use{" "}
            <code class="rounded bg-amber-100 px-1 text-xs">
              Authorization: Bearer
            </code>
            . Use the{" "}
            <code class="rounded bg-amber-100 px-1 text-xs">?secret=</code>{" "}
            query parameter instead — the secret in the URL authenticates each
            request.
          </p>
        </Callout>
      </div>

      <section class="space-y-6">
        <h2 class="text-lg font-semibold text-slate-900">
          Part 1: Configure Shopify as the webhook source
        </h2>

        <Step n={1} title="Create a webhookstorage endpoint">
          <p>
            In your{" "}
            <a href="/dashboard" class="font-medium text-teal-700 underline">
              dashboard
            </a>
            , click <strong>New Endpoint</strong>. Name it something
            descriptive — e.g., <strong>Shopify Orders (prod)</strong> or{" "}
            <strong>Shopify Refunds</strong>. Copy the{" "}
            <strong>Endpoint ID</strong> (starts with <code>ep_</code>) and the{" "}
            <strong>Endpoint secret</strong> (starts with <code>whse_</code>).
          </p>
          <p class="mt-2">
            Use separate endpoints per Shopify event type (orders/create,
            orders/updated, refunds/create). This gives you independent
            notification URLs, rate limits, and event history per workflow.
          </p>
        </Step>

        <Step n={2} title="Build the Shopify-safe ingest URL">
          <p>
            Combine the ingest base URL, your endpoint ID, and secret into a
            single URL:
          </p>
          <CodeSnippet>
            {`${ingestBaseUrl}/{your-endpoint-id}?secret={your-ingest-secret}`}
          </CodeSnippet>
          <p class="mt-2">
            <strong>Treat this URL like a password.</strong> It contains your
            endpoint secret in the query string. Full URLs can appear in Shopify's
            webhook logs, CDN access logs, and reverse-proxy logs. Never share
            it in Slack or commit it to a repo. If you suspect exposure, rotate
            the secret in your endpoint settings — webhookstorage supports two
            active secrets per endpoint for zero-downtime rotation.
          </p>
        </Step>

        <Step n={3} title="Register the webhook in Shopify Admin">
          <ol class="ml-4 mt-1 list-decimal space-y-2">
            <li>
              Open <strong>Settings → Notifications</strong> in your Shopify
              Admin.
            </li>
            <li>
              Scroll to the <strong>Webhooks</strong> section and click{" "}
              <strong>Create webhook</strong>.
            </li>
            <li>
              Choose the event — e.g., <strong>Order creation</strong>,{" "}
              <strong>Order payment</strong>, or{" "}
              <strong>Product update</strong>.
            </li>
            <li>
              Set the format to <strong>JSON</strong>.
            </li>
            <li>
              Paste the full ingest URL (with <code>?secret=</code>) from
              step 2.
            </li>
            <li>
              Select the <strong>latest API version</strong> (e.g., 2025-01).
              Older versions may omit fields your downstream workflow depends on.
            </li>
            <li>
              Click <strong>Save</strong>. Shopify sends a verification
              request — webhookstorage handles this automatically via the
              built-in challenge endpoint.
            </li>
          </ol>
        </Step>

        <Step n={4} title="Send a test webhook">
          <p>
            Click <strong>Send test notification</strong> next to the webhook
            you just created. Check your webhookstorage endpoint's{" "}
            <strong>Events</strong> tab — the test event should appear within
            seconds, with the full payload available for download.
          </p>
          <p class="mt-2">If the test doesn't arrive, check:</p>
          <ul class="ml-4 mt-1 list-disc space-y-1">
            <li>
              The secret in the URL is correct (no trailing spaces or line
              breaks)
            </li>
            <li>
              Shopify's API version is set (not "unstable")
            </li>
            <li>
              Your webhookstorage account is active (not rate-limited or over
              the free tier limit)
            </li>
          </ul>
        </Step>

        <Callout color="slate" title="What about Shopify's HMAC signature?">
          <p>
            Shopify signs every webhook with an{" "}
            <code class="text-xs">X-Shopify-Hmac-Sha256</code> header.
            webhookstorage does <strong>not</strong> verify this — it
            authenticates using the endpoint secret in the URL instead. The
            original Shopify headers and raw body are preserved in S3, so your
            downstream workflow can verify the HMAC after downloading if needed.
          </p>
        </Callout>
      </section>

      <section class="space-y-6">
        <h2 class="text-lg font-semibold text-slate-900">
          Part 2: Connect your downstream automation
        </h2>

        <Step n={5} title="Set the notification URL">
          <p>
            In your endpoint settings, paste your downstream webhook URL as
            the <strong>Notification URL</strong>:
          </p>
          <ul class="ml-4 mt-1 list-disc space-y-1">
            <li>
              <strong>Zapier:</strong> "Webhooks by Zapier" → Catch Hook URL
            </li>
            <li>
              <strong>Make:</strong> Custom Webhook module URL
            </li>
            <li>
              <strong>n8n:</strong> Webhook trigger node URL
            </li>
          </ul>
          <p class="mt-2">
            See the <strong>Make</strong>, <strong>Zapier</strong>, or{" "}
            <strong>n8n</strong> tab for the exact downstream setup.
          </p>
        </Step>

        <Step n={6} title="Understand the notification payload">
          <p>
            Your automation receives a compact JSON notification — not the full
            Shopify order:
          </p>
          <CodeSnippet>
            {`{
  "event_id": "550e8400-e29b-41d4-a716-446655440000",
  "endpoint_id": "ep_shopifyorders",
  "size_bytes": 6291456,
  "content_type": "application/json",
  "received_at": "2026-03-09T12:00:00.000Z",
  "download_url": "https://s3.amazonaws.com/...?X-Amz-Signature=..."
}`}
          </CodeSnippet>
          <p class="mt-2">
            <code class="rounded bg-slate-100 px-1 text-xs">download_url</code>{" "}
            is a presigned S3 link valid for <strong>15 minutes</strong>. A
            simple HTTP GET returns the full Shopify payload — no additional
            authentication needed.
          </p>
        </Step>

        <Step n={7} title="Fetch and process the full payload">
          <p>
            Add an HTTP GET step in your automation that downloads{" "}
            <code class="rounded bg-slate-100 px-1 text-xs">download_url</code>
            . The response is the exact JSON Shopify sent — same fields, same
            structure. Parse it and route to warehouse sync, ERP, fraud review,
            fulfillment, or Slack alerts.
          </p>
        </Step>
      </section>

      <Callout color="slate" title="Common Shopify webhook sizes">
        <div class="space-y-1">
          <p>
            <strong>orders/create:</strong> 50 KB – 10 MB depending on line
            items, metafields, and discount codes.
          </p>
          <p>
            <strong>products/update:</strong> 10 KB – 2 MB, larger with variant
            images and metafields.
          </p>
          <p>
            <strong>customers/update:</strong> Usually under 100 KB, but
            addresses and tags can push this higher.
          </p>
          <p class="mt-2 text-xs text-slate-500">
            webhookstorage accepts payloads up to 250 MB.
          </p>
        </div>
      </Callout>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Make Guide
// ---------------------------------------------------------------------------

function MakeGuide() {
  const ingestBaseUrl = getIngestBaseUrl();

  return (
    <div class="space-y-8">
      <div class="space-y-3">
        <p class="text-sm text-slate-600">
          Make (formerly Integromat) imposes a 5 MB request body limit on its
          Custom Webhook module. When a webhook source sends anything larger,
          Make silently truncates the body — your scenario runs on incomplete
          data without warning. webhookstorage sits in front of Make, stores the
          full payload in S3, and sends Make a tiny notification it can always
          handle.
        </p>
      </div>

      <section class="space-y-6">
        <h2 class="text-lg font-semibold text-slate-900">
          Set up webhookstorage
        </h2>

        <Step n={1} title="Create a webhookstorage endpoint">
          <p>
            In your{" "}
            <a href="/dashboard" class="font-medium text-teal-700 underline">
              dashboard
            </a>
            , click <strong>New Endpoint</strong>. Name it to match the
            workflow — e.g., "Make — Shopify Orders" or "Make — Stripe Events".
            Copy the <strong>Endpoint ID</strong> and{" "}
            <strong>Endpoint secret</strong>.
          </p>
        </Step>

        <Step n={2} title="Point your webhook source at webhookstorage">
          <p>
            In the service sending webhooks (Shopify, GitHub, Stripe, etc.),
            set the webhook URL to your endpoint with the secret in the{" "}
            <code>Authorization</code> header:
          </p>
          <CodeSnippet>
            {`POST ${ingestBaseUrl}/{your-endpoint-id}
Authorization: Bearer {your-ingest-secret}
Content-Type: application/json`}
          </CodeSnippet>
          <p class="mt-2">
            If the source can't set custom headers (like Shopify), use the{" "}
            query-string fallback:{" "}
            <code class="rounded bg-slate-100 px-1 text-xs">
              ?secret={"{your-ingest-secret}"}
            </code>
          </p>
        </Step>
      </section>

      <section class="space-y-6">
        <h2 class="text-lg font-semibold text-slate-900">
          Configure Make
        </h2>

        <Step n={3} title="Create a Custom Webhook trigger in Make">
          <ol class="ml-4 mt-1 list-decimal space-y-2">
            <li>
              In Make, create a new scenario.
            </li>
            <li>
              Add a <strong>Webhooks → Custom webhook</strong> module as the
              first trigger.
            </li>
            <li>
              Click <strong>Add</strong> to create a new webhook. Give it a name
              (e.g., "webhookstorage notifications").
            </li>
            <li>
              Copy the webhook URL Make gives you — it looks like{" "}
              <code class="rounded bg-slate-100 px-1 text-xs">
                https://hook.us1.make.com/abc123...
              </code>
            </li>
            <li>
              Paste this URL as the <strong>Notification URL</strong> in your
              webhookstorage endpoint settings.
            </li>
          </ol>
        </Step>

        <Step n={4} title='Add an "HTTP — Make a request" module'>
          <p>
            After the webhook trigger, add an{" "}
            <strong>HTTP → Make a request</strong> module. Configure it:
          </p>
          <ul class="ml-4 mt-2 list-disc space-y-2">
            <li>
              <strong>URL:</strong> Map{" "}
              <code class="rounded bg-slate-100 px-1 text-xs">
                {"{{1.download_url}}"}
              </code>{" "}
              from the webhook trigger output. In Make's mapping panel, click
              the webhook module (step 1) and select{" "}
              <code>download_url</code>.
            </li>
            <li>
              <strong>Method:</strong> GET
            </li>
            <li>
              <strong>Parse response:</strong> Yes (if you want Make to
              automatically parse the JSON body into fields you can map
              downstream).
            </li>
          </ul>
          <p class="mt-2">
            This module fetches the full payload from S3 — no size limit, no
            auth needed (the signature is in the URL).
          </p>
        </Step>

        <Step n={5} title="Parse and route the data">
          <p>
            If the original webhook body is JSON and you enabled "Parse
            response", the HTTP module output contains the full parsed
            object. Map its fields directly into downstream modules — Google
            Sheets, Airtable, Slack, email, or any Make app.
          </p>
          <p class="mt-2">
            For binary payloads (images, PDFs, archives), set "Parse response"
            to <strong>No</strong> and use a{" "}
            <strong>Google Drive → Upload a file</strong> or{" "}
            <strong>AWS S3 → Upload a file</strong> module to store the raw
            data.
          </p>
        </Step>

        <Step n={6} title="Handle errors">
          <p>
            Add an <strong>error handler</strong> on the HTTP module in case the
            download URL has expired (the signed URL is valid for 15 minutes).
            If your scenario runs on a schedule rather than instantly, the URL
            may expire before execution. To handle this:
          </p>
          <ul class="ml-4 mt-1 list-disc space-y-1">
            <li>
              Set the scenario to run <strong>Immediately</strong> (not on a
              schedule) so it fires as soon as webhookstorage sends the
              notification.
            </li>
            <li>
              If you must use a scheduled trigger, use the{" "}
              <a href="/docs#events" class="font-medium text-teal-700 underline">
                Events API
              </a>{" "}
              to get a fresh download URL before fetching.
            </li>
          </ul>
        </Step>
      </section>

      <Callout color="slate" title="Make-specific limits to know">
        <div class="space-y-1">
          <p>
            <strong>Webhook body:</strong> 5 MB max on Custom Webhook trigger.
            webhookstorage's notification is always under 1 KB.
          </p>
          <p>
            <strong>HTTP module response:</strong> Make's HTTP module can handle
            up to 100 MB responses, so even large payloads download fine.
          </p>
          <p>
            <strong>Operations:</strong> The webhook trigger + HTTP GET counts
            as 2 operations in your Make plan.
          </p>
        </div>
      </Callout>

      <Callout color="teal" title="Test the full flow">
        <p>
          Use the <strong>Test Endpoint</strong> button in your webhookstorage
          dashboard to send a sample payload. In Make, click{" "}
          <strong>Run once</strong> on your scenario — you should see the
          notification arrive at the webhook trigger and the full payload
          downloaded in the HTTP module output.
        </p>
      </Callout>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zapier Guide
// ---------------------------------------------------------------------------

function ZapierGuide() {
  const ingestBaseUrl = getIngestBaseUrl();

  return (
    <div class="space-y-8">
      <div class="space-y-3">
        <p class="text-sm text-slate-600">
          Zapier enforces a 6 MB limit on inbound webhook payloads. When a
          payload exceeds this, Zapier returns an error to the sender and your
          Zap never fires. webhookstorage accepts the full payload (up to
          250 MB), stores it, and sends Zapier a lightweight notification that's
          always within limits.
        </p>
        <Callout color="amber" title="Webhooks by Zapier — not Webhooks">
          <p>
            Zapier has two webhook-related apps. You need{" "}
            <strong>Webhooks by Zapier</strong> (the built-in app with Catch
            Hook). Don't confuse it with third-party "Webhook" apps in the Zapier
            marketplace.
          </p>
        </Callout>
      </div>

      <section class="space-y-6">
        <h2 class="text-lg font-semibold text-slate-900">
          Set up webhookstorage
        </h2>

        <Step n={1} title="Create a webhookstorage endpoint">
          <p>
            In your{" "}
            <a href="/dashboard" class="font-medium text-teal-700 underline">
              dashboard
            </a>
            , click <strong>New Endpoint</strong>. Name it to match the
            workflow — e.g., "Zapier — Form Submissions" or "Zapier — Shopify".
            Copy the <strong>Endpoint ID</strong> and{" "}
            <strong>Endpoint secret</strong>.
          </p>
        </Step>

        <Step n={2} title="Point your webhook source at webhookstorage">
          <p>
            Configure the sending service to POST webhooks to:
          </p>
          <CodeSnippet>
            {`POST ${ingestBaseUrl}/{your-endpoint-id}
Authorization: Bearer {your-ingest-secret}`}
          </CodeSnippet>
          <p class="mt-2">
            If the source can't set custom headers (like Shopify), use{" "}
            <code class="rounded bg-slate-100 px-1 text-xs">
              ?secret={"{your-ingest-secret}"}
            </code>{" "}
            in the URL instead.
          </p>
        </Step>
      </section>

      <section class="space-y-6">
        <h2 class="text-lg font-semibold text-slate-900">
          Configure your Zap
        </h2>

        <Step n={3} title="Create a Catch Hook trigger">
          <ol class="ml-4 mt-1 list-decimal space-y-2">
            <li>
              In Zapier, create a new Zap.
            </li>
            <li>
              Choose <strong>Webhooks by Zapier</strong> as the trigger app.
            </li>
            <li>
              Select <strong>Catch Hook</strong> as the trigger event.
            </li>
            <li>
              Zapier gives you a webhook URL like{" "}
              <code class="rounded bg-slate-100 px-1 text-xs">
                https://hooks.zapier.com/hooks/catch/123456/abcdef/
              </code>
            </li>
            <li>
              Paste this URL as the <strong>Notification URL</strong> in your
              webhookstorage endpoint settings.
            </li>
          </ol>
        </Step>

        <Step n={4} title="Test the trigger">
          <p>
            Click <strong>Test trigger</strong> in Zapier, then send a test
            payload to your webhookstorage endpoint (use the{" "}
            <strong>Test Endpoint</strong> button in your dashboard). Zapier
            should pick up the notification and show you the fields:
          </p>
          <CodeSnippet>
            {`event_id:     550e8400-e29b-41d4-a716-446655440000
endpoint_id:  ep_yourendpoint
size_bytes:   15728640
content_type: application/json
received_at:  2026-03-09T12:00:00.000Z
download_url: https://s3.amazonaws.com/...?X-Amz-Signature=...`}
          </CodeSnippet>
        </Step>

        <Step n={5} title='Add a "GET" action to fetch the full payload'>
          <ol class="ml-4 mt-1 list-decimal space-y-2">
            <li>
              Add a new action step: <strong>Webhooks by Zapier</strong> →{" "}
              <strong>GET</strong>.
            </li>
            <li>
              In the <strong>URL</strong> field, map{" "}
              <code class="rounded bg-slate-100 px-1 text-xs">download_url</code>{" "}
              from the trigger step.
            </li>
            <li>
              Leave <strong>Headers</strong> and <strong>Query String
              Params</strong> empty — the presigned S3 URL includes all
              necessary auth.
            </li>
            <li>
              Test the step. The output should contain the full webhook body
              from the original sender.
            </li>
          </ol>
        </Step>

        <Step n={6} title="Parse the response and continue your Zap">
          <p>
            The GET step returns the raw payload as a string. If it's JSON,
            Zapier automatically parses it into fields you can map downstream.
            Use it in any Zapier action: Google Sheets, Slack, email, database,
            file storage.
          </p>
          <p class="mt-2">
            For large JSON responses, Zapier may show the body as a single
            "data" field. If you need specific nested fields, add a{" "}
            <strong>Formatter by Zapier → Utilities → Line Itemizer</strong>{" "}
            step or a <strong>Code by Zapier</strong> step to parse it.
          </p>
        </Step>
      </section>

      <Callout color="slate" title="Zapier-specific limits to know">
        <div class="space-y-1">
          <p>
            <strong>Catch Hook trigger:</strong> 6 MB max inbound body.
            webhookstorage's notification is always under 1 KB.
          </p>
          <p>
            <strong>GET action response:</strong> Zapier can handle responses up
            to ~50 MB, but processing slows significantly above 10 MB. If your
            payloads are very large, consider using Code by Zapier to extract
            only the fields you need.
          </p>
          <p>
            <strong>Task usage:</strong> The Catch Hook trigger + GET action
            counts as 2 tasks in your Zapier plan.
          </p>
          <p>
            <strong>Execution time:</strong> Zapier has a 30-second timeout per
            step. S3 downloads are fast, but very large files (100 MB+) may need
            a Code step with chunked processing.
          </p>
        </div>
      </Callout>
    </div>
  );
}

// ---------------------------------------------------------------------------
// n8n Guide
// ---------------------------------------------------------------------------

function N8nGuide() {
  const ingestBaseUrl = getIngestBaseUrl();

  return (
    <div class="space-y-8">
      <div class="space-y-3">
        <p class="text-sm text-slate-600">
          n8n's default body size limit is 16 MB (configurable via{" "}
          <code class="rounded bg-slate-100 px-1 text-xs">
            N8N_PAYLOAD_SIZE_MAX
          </code>{" "}
          on self-hosted). Even if you raise the limit, large payloads eat
          memory in the n8n process and slow workflow execution.
          webhookstorage offloads payload storage to S3 and sends n8n a
          lightweight notification that's always fast to process.
        </p>
        <Callout color="blue" title="Self-hosted vs. n8n Cloud">
          <p>
            This guide works for both. The main difference: self-hosted
            instances may sit behind a firewall, so make sure
            webhookstorage's notification POST can reach your n8n Webhook
            node, and that n8n can reach S3 to download payloads via signed
            URLs.
          </p>
        </Callout>
      </div>

      <section class="space-y-6">
        <h2 class="text-lg font-semibold text-slate-900">
          Set up webhookstorage
        </h2>

        <Step n={1} title="Create a webhookstorage endpoint">
          <p>
            In your{" "}
            <a href="/dashboard" class="font-medium text-teal-700 underline">
              dashboard
            </a>
            , click <strong>New Endpoint</strong> and copy the{" "}
            <strong>Endpoint ID</strong> and <strong>Endpoint secret</strong>.
          </p>
        </Step>

        <Step n={2} title="Point your webhook source at webhookstorage">
          <p>
            Configure the sending service to POST to:
          </p>
          <CodeSnippet>
            {`POST ${ingestBaseUrl}/{your-endpoint-id}
Authorization: Bearer {your-ingest-secret}`}
          </CodeSnippet>
        </Step>
      </section>

      <section class="space-y-6">
        <h2 class="text-lg font-semibold text-slate-900">
          Configure n8n
        </h2>

        <Step n={3} title="Create a Webhook trigger node">
          <ol class="ml-4 mt-1 list-decimal space-y-2">
            <li>
              Create a new workflow in n8n.
            </li>
            <li>
              Add a <strong>Webhook</strong> node as the trigger.
            </li>
            <li>
              Set HTTP Method to <strong>POST</strong>.
            </li>
            <li>
              Set Response Code to <strong>200</strong>.
            </li>
            <li>
              Copy the <strong>Production URL</strong> (not the test URL) — it
              looks like{" "}
              <code class="rounded bg-slate-100 px-1 text-xs">
                https://your-n8n.example.com/webhook/abc123
              </code>
            </li>
            <li>
              Paste it as the <strong>Notification URL</strong> in your
              webhookstorage endpoint settings.
            </li>
          </ol>
          <Callout color="amber" title="Use the Production URL">
            <p>
              n8n generates separate Test and Production webhook URLs. Use the{" "}
              <strong>Production</strong> URL so notifications arrive whether or
              not you have the workflow editor open. The test URL only works
              while you're actively listening in the editor.
            </p>
          </Callout>
        </Step>

        <Step n={4} title="Add an HTTP Request node to fetch the payload">
          <p>
            After the Webhook trigger, add an{" "}
            <strong>HTTP Request</strong> node:
          </p>
          <ul class="ml-4 mt-2 list-disc space-y-2">
            <li>
              <strong>Method:</strong> GET
            </li>
            <li>
              <strong>URL:</strong>{" "}
              <code class="rounded bg-slate-100 px-1 text-xs">
                {"{{ $json.download_url }}"}
              </code>
            </li>
            <li>
              <strong>Authentication:</strong> None (the presigned S3 URL
              includes auth)
            </li>
            <li>
              <strong>Response Format:</strong> JSON (if the original payload is
              JSON) or File (for binary data)
            </li>
          </ul>
          <p class="mt-2">
            n8n fetches the full payload directly from S3. No size limits, no
            extra auth.
          </p>
        </Step>

        <Step n={5} title="Process and route the data">
          <p>
            The HTTP Request node outputs the full webhook body. From here, use
            any n8n node:
          </p>
          <ul class="ml-4 mt-1 list-disc space-y-1">
            <li>
              <strong>IF / Switch</strong> to route based on event type or
              payload fields
            </li>
            <li>
              <strong>Set</strong> to extract and reshape specific fields
            </li>
            <li>
              <strong>Postgres / MySQL / MongoDB</strong> to write to your
              database
            </li>
            <li>
              <strong>S3 / Google Drive</strong> to archive binary payloads
            </li>
            <li>
              <strong>Slack / Email</strong> to notify your team
            </li>
          </ul>
        </Step>

        <Step n={6} title="Handle binary payloads">
          <p>
            If the webhook body isn't JSON (images, PDFs, archives), set the
            HTTP Request node's <strong>Response Format</strong> to{" "}
            <strong>File</strong>. The node outputs binary data you can pipe to
            a storage node or process with an Extract node.
          </p>
          <p class="mt-2">
            Check the{" "}
            <code class="rounded bg-slate-100 px-1 text-xs">content_type</code>{" "}
            field in the notification to decide: if it starts with{" "}
            <code>application/json</code> or <code>text/</code>, parse as JSON
            or text. Otherwise, treat as binary.
          </p>
        </Step>
      </section>

      <Callout color="slate" title="n8n-specific tips">
        <div class="space-y-1">
          <p>
            <strong>Execution timeout:</strong> n8n Cloud has a 120-second
            workflow timeout. Self-hosted is configurable via{" "}
            <code class="text-xs">EXECUTIONS_TIMEOUT</code>. S3 downloads are
            fast, but factor this in for very large payloads with complex
            processing.
          </p>
          <p>
            <strong>Memory:</strong> On self-hosted n8n, large HTTP responses
            are held in memory. If you're downloading 100 MB+ payloads,
            allocate at least 1 GB to the n8n process.
          </p>
          <p>
            <strong>Error Workflow:</strong> Set up an{" "}
            <a
              href="https://docs.n8n.io/flow-logic/error-handling/"
              target="_blank"
              rel="noopener noreferrer"
              class="font-medium text-teal-700 underline"
            >
              Error Workflow
            </a>{" "}
            to catch download failures (expired URLs, network issues). Use the{" "}
            <a href="/docs#events" class="font-medium text-teal-700 underline">
              Events API
            </a>{" "}
            to get a fresh download URL if the original expired.
          </p>
          <p>
            <strong>Firewall:</strong> If self-hosted n8n is behind a firewall,
            ensure webhookstorage's notification POST can reach your Webhook
            node and that n8n can make outbound HTTPS requests to S3 for
            payload downloads.
          </p>
        </div>
      </Callout>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Guides() {
  const [platform, setPlatform] = useState<Platform>(() => {
    if (typeof window === "undefined") return DEFAULT_PLATFORM;
    return readPlatformHash(window.location.hash) ?? DEFAULT_PLATFORM;
  });

  useEffect(() => {
    document.title = "Shopify, Zapier, Make & n8n Integration Guides — webhookstorage";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Step-by-step guides for connecting Shopify, Zapier, Make, and n8n to webhookstorage. Buffer large webhook payloads without hitting platform size limits. No CLI required.",
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://webhookstorage.com/guides");

    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const syncFromHash = () => {
      const hashPlatform = readPlatformHash(window.location.hash);
      if (hashPlatform) setPlatform(hashPlatform);
    };

    window.addEventListener("hashchange", syncFromHash);
    syncFromHash();

    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);

  const selectPlatform = (nextPlatform: Platform) => {
    setPlatform(nextPlatform);
    replaceGuideHash(nextPlatform);
  };

  return (
    <div class="min-h-screen bg-white text-slate-900">
      <PublicHeader current="guides" />
      <div class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">

        <h1 class="mb-2 text-3xl font-bold tracking-tight">
          Integration Guides
        </h1>
        <p class="mb-8 text-sm text-slate-500">
          Step-by-step guides for connecting webhook sources and automation
          platforms through webhookstorage. No CLI required.
        </p>

        {/* Platform tabs */}
        <div class="mb-8 flex gap-1 border-b border-slate-200">
          <PlatformTab
            active={platform === "shopify"}
            label="Shopify"
            onClick={() => selectPlatform("shopify")}
          />
          <PlatformTab
            active={platform === "make"}
            label="Make"
            onClick={() => selectPlatform("make")}
          />
          <PlatformTab
            active={platform === "zapier"}
            label="Zapier"
            onClick={() => selectPlatform("zapier")}
          />
          <PlatformTab
            active={platform === "n8n"}
            label="n8n"
            onClick={() => selectPlatform("n8n")}
          />
        </div>

        {/* Guide content */}
        {platform === "shopify" && <ShopifyGuide />}
        {platform === "make" && <MakeGuide />}
        {platform === "zapier" && <ZapierGuide />}
        {platform === "n8n" && <N8nGuide />}

        {/* Common section */}
        <div class="mt-12 space-y-6">
          <h2 class="text-xl font-bold text-slate-900">How it works</h2>
          <div class="grid gap-4 sm:grid-cols-3">
            <div class="rounded-lg border border-slate-200 p-4">
              <div class="mb-2 text-2xl">1</div>
              <h3 class="text-sm font-semibold text-slate-900">Receive</h3>
              <p class="mt-1 text-xs text-slate-500">
                Your webhook source sends payloads to webhookstorage's static
                IP ingest endpoint over TLS.
              </p>
            </div>
            <div class="rounded-lg border border-slate-200 p-4">
              <div class="mb-2 text-2xl">2</div>
              <h3 class="text-sm font-semibold text-slate-900">Store</h3>
              <p class="mt-1 text-xs text-slate-500">
                Payloads are encrypted at rest (AES-256) and stored in isolated
                per-tenant storage. You get a signed download URL.
              </p>
            </div>
            <div class="rounded-lg border border-slate-200 p-4">
              <div class="mb-2 text-2xl">3</div>
              <h3 class="text-sm font-semibold text-slate-900">Forward</h3>
              <p class="mt-1 text-xs text-slate-500">
                A lightweight notification with the download URL is sent to your
                automation platform. It fetches the payload on demand.
              </p>
            </div>
          </div>
        </div>

        <div class="mt-12 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-600">
          See a problem with this guide?{" "}
          <a
            href="mailto:support@webhookstorage.com?subject=Guides%20feedback"
            class="font-medium text-teal-700 underline hover:text-teal-900"
          >
            Please let us know
          </a>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
